/**
 * Mock 数据管理中心
 * 统一管理所有 API 的 mock 数据
 */

import type { BrandAiTrendPoint } from "@/services/api/brandAiMetricsTrend";
import type { BrandMetricsRates } from "@/services/api/brandMetricsRates";
import type { BrandMetricsTrendPoint } from "@/services/api/brandMetricsTrend";
import type {
  ContentCiteItem,
  ContentCiteParams,
} from "@/services/api/contentCite";
import type {
  FeedbackRequest,
  FeedbackResponse,
} from "@/services/api/feedback";
import type { MediaCiteItem, MediaCiteParams } from "@/services/api/mediaCite";
import type { AIModel } from "@/services/api/models";
import type {
  QueryQuestionItem,
  QueryQuestionsParams,
} from "@/services/api/queryQuestions";
import type { QuestionItem, QuestionParams } from "@/services/api/question";
import type {
  ReputationKeywordDetailByKeywordIdData,
  ReputationKeywordDetailByKeywordIdParams,
  ReputationKeywordDetailData,
  ReputationKeywordDetailParams,
} from "@/services/api/reputationDetail";
import type { BrandSearchItem } from "@/types/api";

/**
 * 模拟网络延迟
 */
export const randomDelay = (min = 100, max = 500) => {
  const delay = Math.random() * (max - min) + min;
  return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * AI 模型 Mock 数据
 */
export const mockModels: AIModel[] = [
  {
    id: "257e482f-77ed-11f0-be9b-8a1d1fb6f320",
    ename: "deepseek",
    name: "DeepSeek",
    icon: "https://gen-optima.oss-cn-shanghai.aliyuncs.com/static/images/deepseek.png",
  },
  {
    id: "257e497d-77ed-11f0-be9b-8a1d1fb6f320",
    ename: "doubao",
    name: "豆包",
    icon: "https://gen-optima.oss-cn-shanghai.aliyuncs.com/static/images/doubao.png",
  },
  {
    id: "257e4a24-77ed-11f0-be9b-8a1d1fb6f320",
    ename: "yuanbao",
    name: "腾讯元宝",
    icon: "https://gen-optima.oss-cn-shanghai.aliyuncs.com/static/images/yuanbao.png",
  },
  {
    id: "257e4a7d-77ed-11f0-be9b-8a1d1fb6f320",
    ename: "Perplexity",
    name: "Perplexity",
    icon: "https://gen-optima.oss-cn-shanghai.aliyuncs.com/static/images/Perplexity.png",
  },
  {
    id: "257e4b15-77ed-11f0-be9b-8a1d1fb6f320",
    ename: "wenxin",
    name: "文小言",
    icon: "https://gen-optima.oss-cn-shanghai.aliyuncs.com/static/images/wenxiaoyan.png",
  },
];

/**
 * 品牌搜索 Mock 数据
 */
export const mockBrandSearchData: BrandSearchItem[] = [
  // 理想 - 多行业
  {
    brandId: "58be852f-0c59-4093-ba76-08547d9db5b8",
    brandName: "理想",
    categoryId: "635936db-2e01-e048-878e-e5612650edb3",
    categoryName: "汽车",
  },
  {
    brandId: "58be852f-0c59-4093-ba76-08547d9db5b8",
    brandName: "理想",
    categoryId: "745936db-2e01-e048-878e-e5612650edb4",
    categoryName: "新能源",
  },
  // 蔚来 - 单行业
  {
    brandId: "68be852f-0c59-4093-ba76-08547d9db5b9",
    brandName: "蔚来",
    categoryId: "635936db-2e01-e048-878e-e5612650edb3",
    categoryName: "汽车",
  },
  // 特斯拉 - 单行业
  {
    brandId: "78be852f-0c59-4093-ba76-08547d9db5ba",
    brandName: "特斯拉",
    categoryId: "635936db-2e01-e048-878e-e5612650edb3",
    categoryName: "汽车",
  },
];

// 品牌指标数据缓存
const brandMetricsCache = new Map<string, BrandMetricsRates>();
const brandMetricsTrendCache = new Map<string, BrandMetricsTrendPoint[]>();
const mediaCiteCache = new Map<string, MediaCiteItem[]>();
const brandComparisonCache = new Map<string, BrandComparisonData>();
const contentCiteCache = new Map<string, ContentCiteItem[]>();

export const generateMockBrandMetrics = (
  brandId: string,
  categoryId: string,
  days: number = 7,
  modelId: string = "all"
): BrandMetricsRates => {
  // 使用四个入参作为缓存 key
  const cacheKey = `${brandId}-${categoryId}-${days}-${modelId}`;

  // 先检查缓存
  if (brandMetricsCache.has(cacheKey)) {
    return brandMetricsCache.get(cacheKey)!;
  }

  // 缓存中没有，生成新数据
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days);

  const result: BrandMetricsRates = {
    mentionRate: Number((Math.random() * 30 + 10).toFixed(1)), // 10-40%
    firstMentionRate: Number((Math.random() * 20 + 5).toFixed(1)), // 5-25%
    firstRecommendationRate: Number((Math.random() * 25 + 8).toFixed(1)), // 8-33%
    positiveMentionRate: Number((Math.random() * 15 + 5).toFixed(1)), // 5-20%
    negativeMentionRate: Number((Math.random() * 8 + 1).toFixed(1)), // 1-9%
    statisticsPeriodStart: startDate.toISOString().split("T")[0],
    statisticsPeriodEnd: endDate.toISOString().split("T")[0],
  };

  // 存入缓存
  brandMetricsCache.set(cacheKey, result);

  return result;
};

/**
 * 品牌指标趋势 Mock 数据生成器
 */
// 品牌趋势 API 响应格式
export interface BrandTrendApiResponse {
  code: number;
  message: string;
  data: {
    brandList: {
      curBrand: { brandId: string; brandName: string };
      otherBrand: Array<{ brandId: string; brandName: string }>;
    };
    rateList: {
      [metricType: string]: {
        [brandId: string]: Array<{ day: string; rate: number }>;
      };
    };
  };
}

// 品牌对比数据接口
export interface BrandComparisonData {
  brandList: {
    curBrand: { brandId: string; brandName: string };
    otherBrands: Array<{ brandId: string; brandName: string }>;
  };
  rateList: {
    mentionRate: { [brandId: string]: Array<{ day: string; rate: number }> };
    firstMentionRate: {
      [brandId: string]: Array<{ day: string; rate: number }>;
    };
    firstRecommendationRate: {
      [brandId: string]: Array<{ day: string; rate: number }>;
    };
    positiveMentionRate: {
      [brandId: string]: Array<{ day: string; rate: number }>;
    };
    negativeMentionRate: {
      [brandId: string]: Array<{ day: string; rate: number }>;
    };
  };
}

export const generateMockBrandMetricsTrend = (
  brandId: string,
  categoryId: string,
  days: number = 7,
  metricType: string = "mentionRate",
  modelId: string = "all"
): BrandMetricsTrendPoint[] => {
  // 使用四个入参作为缓存 key
  const cacheKey = `trend-${brandId}-${categoryId}-${days}-${modelId}`;

  // 先检查缓存
  if (brandMetricsTrendCache.has(cacheKey)) {
    return brandMetricsTrendCache.get(cacheKey)!;
  }

  const points: BrandMetricsTrendPoint[] = [];
  const endDate = new Date();

  // 参考 generateMockBrandMetrics 的数据范围
  const baseRanges: Record<string, { min: number; max: number }> = {
    mentionRate: { min: 10, max: 40 }, // 10-40%
    firstMentionRate: { min: 5, max: 25 }, // 5-25%
    firstRecommendationRate: { min: 8, max: 33 }, // 8-33%
    positiveMentionRate: { min: 5, max: 20 }, // 5-20%
    negativeMentionRate: { min: 1, max: 9 }, // 1-9%
  };

  const range = baseRanges[metricType] || baseRanges.mentionRate;
  let mentionValue = (range.min + range.max) / 2;

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - i);

    // 添加随机波动，参考 generateMockBrandMetrics 的随机范围
    const variation = (Math.random() - 0.5) * 4; // ±2%
    mentionValue = Math.max(
      range.min,
      Math.min(range.max, mentionValue + variation)
    );

    // 基于 mentionRate 计算其他指标，保持与 generateMockBrandMetrics 一致的比例
    const firstMentionValue = mentionValue * (0.5 + Math.random() * 0.2); // 50-70%
    const firstRecommendValue = mentionValue * (0.7 + Math.random() * 0.2); // 70-90%
    const positiveMentionValue = mentionValue * (0.5 + Math.random() * 0.3); // 50-80%
    const negativeMentionValue = mentionValue * (0.05 + Math.random() * 0.1); // 5-15%

    points.push({
      date: date.toISOString().split("T")[0],
      mentionRate: Number(mentionValue.toFixed(1)),
      firstMentionRate: Number(firstMentionValue.toFixed(1)),
      firstRecommendationRate: Number(firstRecommendValue.toFixed(1)),
      positiveMentionRate: Number(positiveMentionValue.toFixed(1)),
      negativeMentionRate: Number(negativeMentionValue.toFixed(1)),
    });
  }

  // 存入缓存
  brandMetricsTrendCache.set(cacheKey, points);

  return points;
};

/**
 * 生成品牌对比数据（包含当前品牌和竞品的完整数据）
 */
export const generateMockBrandComparisonData = (
  currentBrandId: string,
  categoryId: string,
  days: number = 7,
  // metricType: string = 'mentionRate,firstMentionRate,firstRecommendationRate',
  modelId: string = "all"
): BrandComparisonData => {
  // 使用四个入参作为缓存 key
  const cacheKey = `comparison-${currentBrandId}-${categoryId}-${days}-${modelId}`;

  // 先检查缓存
  if (brandComparisonCache.has(cacheKey)) {
    return brandComparisonCache.get(cacheKey)!;
  }
  // 根据分类生成竞品
  const competitorsByCategory: Record<
    string,
    Array<{ brandId: string; brandName: string }>
  > = {
    // 汽车分类竞品
    "635936db-2e01-e048-878e-e5612650edb3": [
      { brandId: "58be852f-0c59-4093-ba76-08547d9db5b8", brandName: "理想" },
      { brandId: "68be852f-0c59-4093-ba76-08547d9db5b9", brandName: "丰田" },
      { brandId: "78be852f-0c59-4093-ba76-08547d9db5ba", brandName: "本田" },
      { brandId: "88be852f-0c59-4093-ba76-08547d9db5bb", brandName: "日产" },
      { brandId: "98be852f-0c59-4093-ba76-08547d9db5bc", brandName: "福特" },
      { brandId: "a8be852f-0c59-4093-ba76-08547d9db5bd", brandName: "宝马" },
      { brandId: "b8be852f-0c59-4093-ba76-08547d9db5be", brandName: "奔驰" },
      { brandId: "c8be852f-0c59-4093-ba76-08547d9db5bf", brandName: "雷诺" },
      { brandId: "d8be852f-0c59-4093-ba76-08547d9db5c0", brandName: "沃尔沃" },
      { brandId: "e8be852f-0c59-4093-ba76-08547d9db5c1", brandName: "雪佛兰" },
    ],
    // 新能源分类竞品
    "745936db-2e01-e048-878e-e5612650edb4": [
      { brandId: "58be852f-0c59-4093-ba76-08547d9db5b8", brandName: "理想" },
      { brandId: "68be852f-0c59-4093-ba76-08547d9db5b9", brandName: "蔚来" },
      { brandId: "78be852f-0c59-4093-ba76-08547d9db5ba", brandName: "特斯拉" },
      { brandId: "88be852f-0c59-4093-ba76-08547d9db5bb", brandName: "比亚迪" },
      { brandId: "98be852f-0c59-4093-ba76-08547d9db5bc", brandName: "小鹏" },
      { brandId: "a8be852f-0c59-4093-ba76-08547d9db5bd", brandName: "零跑" },
      { brandId: "b8be852f-0c59-4093-ba76-08547d9db5be", brandName: "问界" },
      { brandId: "c8be852f-0c59-4093-ba76-08547d9db5bf", brandName: "极氪" },
      { brandId: "d8be852f-0c59-4093-ba76-08547d9db5c2", brandName: "智己" },
      { brandId: "e8be852f-0c59-4093-ba76-08547d9db5c3", brandName: "飞凡" },
    ],
  };

  // 如果分类ID不匹配，使用默认的竞品列表
  const allBrands = competitorsByCategory[categoryId] || [
    { brandId: "58be852f-0c59-4093-ba76-08547d9db5b8", brandName: "理想" },
    { brandId: "68be852f-0c59-4093-ba76-08547d9db5b9", brandName: "蔚来" },
    { brandId: "78be852f-0c59-4093-ba76-08547d9db5ba", brandName: "特斯拉" },
    { brandId: "88be852f-0c59-4093-ba76-08547d9db5bb", brandName: "比亚迪" },
    { brandId: "98be852f-0c59-4093-ba76-08547d9db5bc", brandName: "小鹏" },
    { brandId: "a8be852f-0c59-4093-ba76-08547d9db5bd", brandName: "零跑" },
    { brandId: "b8be852f-0c59-4093-ba76-08547d9db5be", brandName: "问界" },
    { brandId: "c8be852f-0c59-4093-ba76-08547d9db5bf", brandName: "极氪" },
    { brandId: "d8be852f-0c59-4093-ba76-08547d9db5c2", brandName: "智己" },
    { brandId: "e8be852f-0c59-4093-ba76-08547d9db5c3", brandName: "飞凡" },
  ];

  const currentBrand = allBrands.find((b) => b.brandId === currentBrandId) || {
    brandId: currentBrandId,
    brandName: currentBrandId,
  };
  const otherBrands = allBrands.filter((b) => b.brandId !== currentBrandId);

  // 生成时间序列
  const endDate = new Date();
  const timeLabels: string[] = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - (days - 1 - i));
    timeLabels.push(date.toISOString().split("T")[0]);
  }

  // 对齐 generateMockBrandMetricsTrend 的构造逻辑
  const baseRanges: Record<string, { min: number; max: number }> = {
    mentionRate: { min: 10, max: 40 }, // 10-40%
    firstMentionRate: { min: 5, max: 25 }, // 5-25%
    firstRecommendationRate: { min: 8, max: 33 }, // 8-33%
    positiveMentionRate: { min: 5, max: 20 }, // 5-20%
    negativeMentionRate: { min: 1, max: 9 }, // 1-9%
  };

  // 根据是否当前品牌设定初始基线，当前品牌略高
  const createSeriesForBrand = (isCurrentBrand: boolean) => {
    const data: Record<string, Array<{ day: string; rate: number }>> = {
      mentionRate: [],
      firstMentionRate: [],
      firstRecommendationRate: [],
      positiveMentionRate: [],
      negativeMentionRate: [],
    };

    let mentionValue =
      (baseRanges.mentionRate.min + baseRanges.mentionRate.max) / 2;
    // 当前品牌基线微调高一些，竞品牌线微调低一些
    mentionValue += isCurrentBrand ? 4 : -2;

    timeLabels.forEach((day) => {
      // 随机游走，幅度与趋势生成器保持一致（±2%）
      const variation = (Math.random() - 0.5) * 4;
      mentionValue = Math.max(
        baseRanges.mentionRate.min,
        Math.min(baseRanges.mentionRate.max, mentionValue + variation)
      );

      // 派生其他指标，系数与趋势生成器一致/相近
      const firstMentionValue = mentionValue * (0.5 + Math.random() * 0.2); // 50-70%
      const firstRecommendValue = mentionValue * (0.7 + Math.random() * 0.2); // 70-90%
      const positiveMentionValue = mentionValue * (0.5 + Math.random() * 0.3); // 50-80%
      const negativeMentionValue = mentionValue * (0.05 + Math.random() * 0.1); // 5-15%

      data.mentionRate.push({ day, rate: Number(mentionValue.toFixed(1)) });
      data.firstMentionRate.push({
        day,
        rate: Number(firstMentionValue.toFixed(1)),
      });
      data.firstRecommendationRate.push({
        day,
        rate: Number(firstRecommendValue.toFixed(1)),
      });
      data.positiveMentionRate.push({
        day,
        rate: Number(positiveMentionValue.toFixed(1)),
      });
      data.negativeMentionRate.push({
        day,
        rate: Number(negativeMentionValue.toFixed(1)),
      });
    });

    return data;
  };

  // 构建完整的 rateList
  const rateList: BrandComparisonData["rateList"] = {
    mentionRate: {},
    firstMentionRate: {},
    firstRecommendationRate: {},
    positiveMentionRate: {},
    negativeMentionRate: {},
  };

  // 当前品牌时间序列
  const currentSeries = createSeriesForBrand(true);
  rateList.mentionRate[currentBrand.brandId] = currentSeries.mentionRate;
  rateList.firstMentionRate[currentBrand.brandId] =
    currentSeries.firstMentionRate;
  rateList.firstRecommendationRate[currentBrand.brandId] =
    currentSeries.firstRecommendationRate;
  rateList.positiveMentionRate[currentBrand.brandId] =
    currentSeries.positiveMentionRate;
  rateList.negativeMentionRate[currentBrand.brandId] =
    currentSeries.negativeMentionRate;

  // 竞品时间序列
  otherBrands.forEach((brand) => {
    const competitorSeries = createSeriesForBrand(false);
    rateList.mentionRate[brand.brandId] = competitorSeries.mentionRate;
    rateList.firstMentionRate[brand.brandId] =
      competitorSeries.firstMentionRate;
    rateList.firstRecommendationRate[brand.brandId] =
      competitorSeries.firstRecommendationRate;
    rateList.positiveMentionRate[brand.brandId] =
      competitorSeries.positiveMentionRate;
    rateList.negativeMentionRate[brand.brandId] =
      competitorSeries.negativeMentionRate;
  });

  const result = {
    brandList: {
      curBrand: currentBrand,
      otherBrands,
    },
    rateList,
  };

  // 存入缓存
  brandComparisonCache.set(cacheKey, result);

  return result;
};

/**
 * AI 平台趋势 Mock 数据生成器
 */
export const generateMockBrandAiTrend = (
  days: number = 7
): BrandAiTrendPoint[] => {
  const platforms = ["DeepSeek", "豆包", "元宝", "Perplexity", "文小言"];
  const points: BrandAiTrendPoint[] = [];
  const endDate = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - i);

    // 为每个平台生成数据
    platforms.forEach((platform) => {
      //const mentionRate = Math.random() * 20 + 5 // 5-25%
      const mentionRate = Math.random() * 20 + 10;
      points.push({
        date: date.toISOString().split("T")[0],
        platform,
        mentionRate: Number(mentionRate.toFixed(1)),
        firstMentionRate: Number((mentionRate * 0.6).toFixed(1)),
        firstRecommendationRate: Number((mentionRate * 0.8).toFixed(1)),
      });
    });
  }

  return points;
};

/**
 * Mock 数据管理器
 */
/**
 * 生成符合 API 格式的品牌趋势数据
 */
export const generateMockBrandTrendApiResponse = (
  brandId: string,
  brandName: string,
  categoryId: string,
  days: number = 7
): BrandTrendApiResponse => {
  // 根据分类获取竞品
  const competitorsByCategory: Record<
    string,
    Array<{ brandId: string; brandName: string }>
  > = {
    "635936db-2e01-e048-878e-e5612650edb3": [
      { brandId: "58be852f-0c59-4093-ba76-08547d9db5b8", brandName: "理想" },
      { brandId: "68be852f-0c59-4093-ba76-08547d9db5b9", brandName: "蔚来" },
      { brandId: "78be852f-0c59-4093-ba76-08547d9db5ba", brandName: "特斯拉" },
      { brandId: "88be852f-0c59-4093-ba76-08547d9db5bb", brandName: "比亚迪" },
    ],
    "745936db-2e01-e048-878e-e5612650edb4": [
      { brandId: "58be852f-0c59-4093-ba76-08547d9db5b8", brandName: "理想" },
      { brandId: "68be852f-0c59-4093-ba76-08547d9db5b9", brandName: "蔚来" },
      { brandId: "78be852f-0c59-4093-ba76-08547d9db5ba", brandName: "特斯拉" },
      { brandId: "88be852f-0c59-4093-ba76-08547d9db5bb", brandName: "比亚迪" },
    ],
  };

  const allBrands = competitorsByCategory[categoryId] || [];
  const currentBrand = allBrands.find((b) => b.brandId === brandId) || {
    brandId,
    brandName,
  };
  const otherBrands = allBrands
    .filter((b) => b.brandId !== brandId)
    .slice(0, 3);

  // 生成时间序列
  const endDate = new Date();
  const timeLabels: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - i);
    timeLabels.push(date.toISOString().split("T")[0]);
  }

  // 生成指标数据
  const metrics = [
    "mentionRate",
    "firstMentionRate",
    "firstRecommendationRate",
    "positiveMentionRate",
    "negativeMentionRate",
  ];
  const rateList: BrandTrendApiResponse["data"]["rateList"] = {};

  metrics.forEach((metric) => {
    rateList[metric] = {};

    const baseValue =
      metric === "positiveMentionRate"
        ? 80
        : metric === "negativeMentionRate"
          ? 10
          : 25;
    const currentBrandData = timeLabels.map((day) => {
      const variation = (Math.random() - 0.5) * 10;
      const rate = Math.max(0, Math.min(100, baseValue + variation));
      return { day, rate: Number(rate.toFixed(2)) };
    });
    rateList[metric][currentBrand.brandId] = currentBrandData;

    otherBrands.forEach((competitor) => {
      const competitorBase = baseValue * (0.7 + Math.random() * 0.6);
      const competitorData = timeLabels.map((day) => {
        const variation = (Math.random() - 0.5) * 8;
        const rate = Math.max(0, Math.min(100, competitorBase + variation));
        return { day, rate: Number(rate.toFixed(2)) };
      });
      rateList[metric][competitor.brandId] = competitorData;
    });
  });

  return {
    code: 200,
    message: "success",
    data: {
      brandList: {
        curBrand: currentBrand,
        otherBrand: otherBrands,
      },
      rateList,
    },
  };
};

export const mockManager = {
  /**
   * 获取模型列表
   */
  async getModels(): Promise<AIModel[]> {
    await randomDelay();
    return [...mockModels];
  },

  /**
   * 搜索品牌
   */
  async searchBrands(keyword?: string): Promise<BrandSearchItem[]> {
    await randomDelay();

    if (!keyword) return [...mockBrandSearchData];

    const q = keyword.trim().toLowerCase();
    return mockBrandSearchData.filter(
      (item) =>
        item.brandName.toLowerCase().includes(q) ||
        item.categoryName.toLowerCase().includes(q)
    );
  },

  /**
   * 获取品牌指标
   */
  async getBrandMetrics(
    _brandId: string,
    _categoryId: string,
    days = 7,
    _modelId = "all"
  ): Promise<BrandMetricsRates> {
    await randomDelay();
    return generateMockBrandMetrics(_brandId, _categoryId, days, _modelId);
  },

  /**
   * 获取品牌指标趋势
   */
  async getBrandMetricsTrend(
    _brandId: string,
    _categoryId: string,
    days = 7,
    metricType: string = "mentionRate",
    modelId: string = "all"
  ): Promise<BrandMetricsTrendPoint[]> {
    await randomDelay();
    return generateMockBrandMetricsTrend(
      _brandId,
      _categoryId,
      days,
      metricType,
      modelId
    );
  },

  /**
   * 获取品牌对比数据（包含当前品牌和竞品）
   */
  async getBrandComparisonData(
    brandId: string,
    categoryId: string,
    days = 7,
    // metricType: string = 'mentionRate,firstMentionRate,firstRecommendationRate',
    modelId: string = "all"
  ): Promise<BrandComparisonData> {
    await randomDelay();
    return generateMockBrandComparisonData(brandId, categoryId, days, modelId);
  },

  /**
   * 获取 AI 平台趋势
   */
  async getAiPlatformTrend(
    _brandId: string,
    _categoryId: string,
    days = 7,
    _metricType: "mentionRate" = "mentionRate"
  ): Promise<BrandAiTrendPoint[]> {
    await randomDelay();
    return generateMockBrandAiTrend(days);
  },

  /**
   * 获取 品牌问题回答结果（关键词详情） Mock
   */
  async getReputationKeywordDetail(
    params: ReputationKeywordDetailParams
  ): Promise<ReputationKeywordDetailData> {
    await randomDelay();
    const now = Date.now();
    const answer = `[TaskID: ${params.taskId}] 考虑到不同预算和用车场景的"最佳选择"会有所不同，我为你整理了几个在销量、技术或服务上表现突出的品牌，你可以根据最看重的方面进行选择。

下面这个表格梳理了这些品牌的核心优势和适合人群，帮你快速了解。

品牌\t核心优势\t主要推荐理由\t适合人群
比亚迪\t综合性价比与市场占有率\t全球销量领先，技术成熟（如刀片电池），产品线极其丰富\t追求稳妥、可靠，看重性价比和大众认可度的消费者
特斯拉\t智能驾驶与充电网络\t行业领先的Autopilot智能驾驶系统和覆盖广泛的超充网络\t热衷前沿科技，重视智能驾驶体验和充电便利性的用户
蔚来\t高端服务与用户体验\t在权威评测中荣获售后服务满意度双料冠军，提供换电等独特服务\t预算充足，对服务品质有极高要求，看重全程用车体验的高端用户
小鹏汽车\t智能科技的性价比\t在智能驾驶（如XNGP系统）和智能座舱方面表现突出，产品科技感强\t科技爱好者，希望以相对亲民的价格享受到先进智能驾驶功能

💡 如何选择适合你的品牌

在做最终决定前，你还可以从以下几个方面进行考量：

• 明确核心需求：想清楚你买车最看重的是什么。是省心可靠、技术成熟（比亚迪），是追求极致的科技体验（特斯拉、小鹏），还是渴望尊贵、无忧的服务（蔚来）？

• 关注核心技术：除了表格中提到的，还可以深入了解各家的电池技术、超充速度、智能驾驶的本土化适应能力等。例如，支持800V高压平台的车型能获得更快的充电速度。

• 参考权威评价：除了销量，也可以参考像J.D. Power这类权威机构发布的售后服务满意度或新车质量研究等报告，它们能从不同维度反映品牌的长期表现。

• 亲自体验：务必去线下门店实际看车、试乘试驾。亲自感受车辆的做工、空间、车机系统的流畅度以及驾驶质感，这是判断一款车是否适合你的最关键一步。

希望以上分析能帮你找到最心仪的品牌。如果你能告诉我你的具体预算或者主要的用车场景（例如，主要是城市代步，还是经常有长途需求），我可以为你提供更精准的车型推荐。`;

    return {
      taskId: params.taskId,
      questionId: "0199892b-c1e7-707e-88bd-ff13ce7eede3",
      question: "用户对主流新能源品牌的喜爱和抱怨点",
      answer,
      modelId: params.modelId || "257e482f-77ed-11f0-be9b-8a1d1fb6f320",
      modelName: "Deepseek",
      createTime: now,
      linksNum: Math.floor(Math.random() * 10) + 3,
    };
  },

  // 新增：通过keywordId获取详情的mock方法
  async getReputationKeywordDetailByKeywordId(
    params: ReputationKeywordDetailByKeywordIdParams
  ): Promise<ReputationKeywordDetailData> {
    await randomDelay();
    const now = Date.now();
    const answer = `[KeywordID: ${params.keywordId}] 考虑到不同预算和用车场景的"最佳选择"会有所不同，我为你整理了几个在销量、技术或服务上表现突出的品牌，你可以根据最看重的方面进行选择。

下面这个表格梳理了这些品牌的核心优势和适合人群，帮你快速了解。

品牌\t核心优势\t主要推荐理由\t适合人群
比亚迪\t综合性价比与市场占有率\t全球销量领先，技术成熟（如刀片电池），产品线极其丰富\t追求稳妥、可靠，看重性价比和大众认可度的消费者
特斯拉\t智能驾驶与充电网络\t行业领先的Autopilot智能驾驶系统和覆盖广泛的超充网络\t热衷前沿科技，重视智能驾驶体验和充电便利性的用户
蔚来\t高端服务与用户体验\t在权威评测中荣获售后服务满意度双料冠军，提供换电等独特服务\t预算充足，对服务品质有极高要求，看重全程用车体验的高端用户
小鹏汽车\t智能科技的性价比\t在智能驾驶（如XNGP系统）和智能座舱方面表现突出，产品科技感强\t科技爱好者，希望以相对亲民的价格享受到先进智能驾驶功能

💡 如何选择适合你的品牌

在做最终决定前，你还可以从以下几个方面进行考量：

• 明确核心需求：想清楚你买车最看重的是什么。是省心可靠、技术成熟（比亚迪），是追求极致的科技体验（特斯拉、小鹏），还是渴望尊贵、无忧的服务（蔚来）？

• 关注核心技术：除了表格中提到的，还可以深入了解各家的电池技术、超充速度、智能驾驶的本土化适应能力等。例如，支持800V高压平台的车型能获得更快的充电速度。

• 参考权威评价：除了销量，也可以参考像J.D. Power这类权威机构发布的售后服务满意度或新车质量研究等报告，它们能从不同维度反映品牌的长期表现。

• 亲自体验：务必去线下门店实际看车、试乘试驾。亲自感受车辆的做工、空间、车机系统的流畅度以及驾驶质感，这是判断一款车是否适合你的最关键一步。

希望以上分析能帮你找到最心仪的品牌。如果你能告诉我你的具体预算或者主要的用车场景（例如，主要是城市代步，还是经常有长途需求），我可以为你提供更精准的车型推荐。`;

    return {
      taskId: `task-${params.keywordId}-${Date.now()}`, // 生成一个基于keywordId的taskId
      questionId: `question-${params.keywordId}-${Date.now()}`, // 生成一个基于keywordId的questionId
      question: "用户对主流新能源品牌的喜爱和抱怨点",
      answer,
      modelId: params.modelId || "257e482f-77ed-11f0-be9b-8a1d1fb6f320",
      modelName: "Deepseek",
      createTime: now,
      linksNum: Math.floor(Math.random() * 10) + 3,
    };
  },

  // 新增：通过keywordId获取详情的mock方法（支持分页）
  async getReputationKeywordDetailByKeywordIdWithPagination(
    params: ReputationKeywordDetailByKeywordIdParams & {
      page?: number;
      pageSize?: number;
    }
  ): Promise<ReputationKeywordDetailByKeywordIdData> {
    await randomDelay();

    const page = params.page || 1;
    const pageSize = params.pageSize || 10;
    const total = 11; // 模拟总条数为11

    // 生成模拟数据列表
    const list: ReputationKeywordDetailData[] = [];
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);

    for (let i = startIndex; i < endIndex; i++) {
      const now = Date.now();
      const answer = `[KeywordID: ${params.keywordId}, Page: ${page}, Index: ${i}] 考虑到不同预算和用车场景的"最佳选择"会有所不同，我为你整理了几个在销量、技术或服务上表现突出的品牌，你可以根据最看重的方面进行选择。

下面这个表格梳理了这些品牌的核心优势和适合人群，帮你快速了解。

品牌\t核心优势\t主要推荐理由\t适合人群
比亚迪\t综合性价比与市场占有率\t全球销量领先，技术成熟（如刀片电池），产品线极其丰富\t追求稳妥、可靠，看重性价比和大众认可度的消费者
特斯拉\t智能驾驶与充电网络\t行业领先的Autopilot智能驾驶系统和覆盖广泛的超充网络\t热衷前沿科技，重视智能驾驶体验和充电便利性的用户
蔚来\t高端服务与用户体验\t在权威评测中荣获售后服务满意度双料冠军，提供换电等独特服务\t预算充足，对服务品质有极高要求，看重全程用车体验的高端用户
小鹏汽车\t智能科技的性价比\t在智能驾驶（如XNGP系统）和智能座舱方面表现突出，产品科技感强\t科技爱好者，希望以相对亲民的价格享受到先进智能驾驶功能

💡 如何选择适合你的品牌

在做最终决定前，你还可以从以下几个方面进行考量：

• 明确核心需求：想清楚你买车最看重的是什么。是省心可靠、技术成熟（比亚迪），是追求极致的科技体验（特斯拉、小鹏），还是渴望尊贵、无忧的服务（蔚来）？

• 关注核心技术：除了表格中提到的，还可以深入了解各家的电池技术、超充速度、智能驾驶的本土化适应能力等。例如，支持800V高压平台的车型能获得更快的充电速度。

• 参考权威评价：除了销量，也可以参考像J.D. Power这类权威机构发布的售后服务满意度或新车质量研究等报告，它们能从不同维度反映品牌的长期表现。

• 亲自体验：务必去线下门店实际看车、试乘试驾。亲自感受车辆的做工、空间、车机系统的流畅度以及驾驶质感，这是判断一款车是否适合你的最关键一步。

希望以上分析能帮你找到最心仪的品牌。如果你能告诉我你的具体预算或者主要的用车场景（例如，主要是城市代步，还是经常有长途需求），我可以为你提供更精准的车型推荐。`;

      list.push({
        taskId: `task-${params.keywordId}-${i}-${Date.now()}`, // 生成一个基于keywordId和索引的taskId
        questionId: `question-${params.keywordId}-${i}-${Date.now()}`, // 生成一个基于keywordId和索引的questionId
        question: `与"${params.keywordId}"相关的问答 - 第${i + 1}条`,
        answer,
        modelId: params.modelId || "257e482f-77ed-11f0-be9b-8a1d1fb6f320",
        modelName: "Deepseek",
        createTime: now,
        linksNum: Math.floor(Math.random() * 10) + 3,
        // 添加缺失的字段，确保CitelistModal能正确获取数据
        brandId: params.brandId,
        categoryId: params.categoryId,
        days: params.days,
        metricType: params.metricType,
      });
    }

    return {
      list,
      total,
      page,
      pageSize,
    };
  },

  /**
   * 品牌问题回答引用信源列表 Mock 数据
   */
  async getQuestionCiteList(params: any): Promise<{
    taskId: string;
    list: any[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    await randomDelay();

    const { taskId, page = 1, pageSize = 20 } = params;

    // 为每个taskId维护一个固定的数据集
    // 使用taskId作为种子生成固定的数据
    let seed = 0;
    if (taskId) {
      const chars: string[] = Array.from(taskId);
      seed = chars.reduce(
        (acc: number, char: string) => acc + char.charCodeAt(0),
        0
      );
    }

    // 生成固定数量的引用信源列表数据（基于taskId的种子）
    const citeList = Array.from({ length: 25 }, (_, index) => {
      // 使用种子值来确保相同taskId生成相同的数据
      const titles = [
        "新能源轿跑对比评测",
        "电动轿跑续航测试",
        "特斯拉在中国市场表现强劲",
        "比亚迪刀片电池技术突破",
        "蔚来换电网络覆盖全国主要城市",
        "理想汽车智能驾驶技术分析",
        "小鹏汽车充电网络建设",
        "新能源汽车政策支持解读",
        "电动汽车技术发展趋势",
        "智能网联汽车发展前景",
        "新能源汽车电池技术革新",
        "自动驾驶技术应用现状",
        "新能源汽车市场格局分析",
        "电动汽车充电基础设施建设",
        "新能源汽车用户满意度调查",
        "智能座舱技术发展分析",
        "新能源汽车产业链研究",
        "电动汽车安全性能评估",
        "新能源汽车品牌影响力分析",
        "智能交通系统发展前景",
        "新能源汽车环保效益研究",
        "电动汽车续航技术突破",
        "新能源汽车价格竞争力分析",
        "智能汽车操作系统发展",
        "新能源汽车国际化战略",
      ];

      const sites = [
        { name: "汽车之家", icon: "https://example.com/icon1.png" },
        { name: "懂车帝", icon: "https://example.com/icon2.png" },
        { name: "腾讯财经", icon: "https://example.com/icon3.png" },
        { name: "凤凰科技", icon: "https://example.com/icon4.png" },
        { name: "搜狐汽车", icon: "https://example.com/icon5.png" },
        { name: "新浪汽车", icon: "https://example.com/icon6.png" },
        { name: "网易汽车", icon: "https://example.com/icon7.png" },
        { name: "36氪", icon: "https://example.com/icon8.png" },
        { name: "虎嗅", icon: "https://example.com/icon9.png" },
        { name: "钛媒体", icon: "https://example.com/icon10.png" },
      ];

      // 使用种子值和索引来生成确定性的结果
      const titleIndex = (index + seed) % titles.length;
      const siteIndex = (index + seed) % sites.length;
      const dayOffset = (index + seed) % 7;
      const publishDate = new Date(2025, 9, 15 + dayOffset); // 10月15日开始

      return {
        title: `${index + 1}:${titles[titleIndex]}`,
        url: `https://www.baidu.com/s?wd=${encodeURIComponent(titles[titleIndex])}`,
        snippet: `这是第${index + 1}条引用数据的摘要内容，详细介绍了${titles[titleIndex]}的相关信息和分析。`,
        siteName: sites[siteIndex].name,
        siteIcon: sites[siteIndex].icon,
        citeIndex: index + 1,
        publishTime: publishDate.toISOString().split("T")[0],
      };
    });

    // 分页处理
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const list = citeList.slice(startIndex, endIndex);

    console.log(
      `[Mock] getQuestionCiteList for taskId: ${taskId}, page: ${page}, pageSize: ${pageSize}, returning ${list.length} items`
    );

    return {
      taskId,
      list,
      total: citeList.length,
      page,
      pageSize,
    };
  },

  /**
   * 媒体引用分析 Mock
   */
  async getMediaCite(params: MediaCiteParams): Promise<MediaCiteItem[]> {
    await randomDelay();
    // 缓存 key
    const brandId = params.brandId || "all";
    const categoryId = params.categoryId;
    const days = params.days || 1;
    const modelId = params.modelId || "all";
    const cacheKey = `${brandId}-${categoryId}-${days}-${modelId}`;

    // 命中缓存
    if (mediaCiteCache.has(cacheKey)) {
      return mediaCiteCache.get(cacheKey) as MediaCiteItem[];
    }

    // 根据 categoryId/days 简单扰动，保持稳定又略有变化
    const seed = (categoryId || "cat").length + days;
    const base: MediaCiteItem[] = [
      {
        site_name: "新浪财经网",
        domain: "cj.sina.com.cn",
        domainId: "domain-1",
        rate: 34.65,
      },
      {
        site_name: "腾讯新闻",
        domain: "news.qq.com",
        domainId: "domain-2",
        rate: 28.33,
      },
      {
        site_name: "网易新闻",
        domain: "news.163.com",
        domainId: "domain-3",
        rate: 15.42,
      },
      {
        site_name: "凤凰网",
        domain: "ifeng.com",
        domainId: "domain-4",
        rate: 9.12,
      },
      {
        site_name: "搜狐新闻",
        domain: "sohu.com",
        domainId: "domain-5",
        rate: 6.88,
      },
    ];
    // 轻微抖动
    const data = base.map((it, idx) => ({
      ...it,
      rate: Number(
        (it.rate * (1 + (((seed + idx) % 3) - 1) * 0.01)).toFixed(2)
      ),
    }));
    mediaCiteCache.set(cacheKey, data);
    return data;
  },

  /**
   * 内容引用分析 Mock
   */
  async getContentCite(params: ContentCiteParams): Promise<ContentCiteItem[]> {
    await randomDelay();
    // 缓存 key
    const brandId = params.brandId || "all";
    const categoryId = params.categoryId;
    const days = params.days || 1;
    const modelId = params.modelId || "all";
    const cacheKey = `content-${brandId}-${categoryId}-${days}-${modelId}`;

    // 命中缓存
    if (contentCiteCache.has(cacheKey)) {
      return contentCiteCache.get(cacheKey) as ContentCiteItem[];
    }

    // 根据 categoryId/days 简单扰动，保持稳定又略有变化
    const seed = (categoryId || "cat").length + days;
    const base: ContentCiteItem[] = [
      { title: "理想汽车2024年财报分析", site_name: "新浪财经网", rate: 34.65 },
      { title: "电动汽车市场趋势报告", site_name: "腾讯新闻", rate: 28.32 },
      { title: "新能源汽车技术发展现状", site_name: "网易新闻", rate: 22.15 },
      { title: "智能驾驶发展前景", site_name: "凤凰网", rate: 18.76 },
      { title: "电池技术革新研究", site_name: "搜狐新闻", rate: 15.42 },
      { title: "充电基础设施建设", site_name: "财经网", rate: 12.88 },
      { title: "新能源汽车政策解读", site_name: "第一财经", rate: 10.25 },
      { title: "自动驾驶安全标准", site_name: "科技日报", rate: 8.67 },
    ];

    // 轻微抖动，保持数据稳定性
    const data = base.map((it, idx) => ({
      ...it,
      rate: Number(
        (it.rate * (1 + (((seed + idx) % 3) - 1) * 0.02)).toFixed(2)
      ),
    }));

    contentCiteCache.set(cacheKey, data);
    return data;
  },

  // 生成问题列表 mock 数据
  async getQuestionList(params: QuestionParams): Promise<{
    list: QuestionItem[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    await randomDelay();

    const { page = 1, pageSize = 20, metricType } = params;

    // 根据 metricType 生成不同的问题
    const questionTemplates = {
      mentionRate: [
        "1:用户对主流新能源品牌的喜爱和抱怨点",
        "2:新能源汽车市场发展趋势分析",
        "3:电动汽车技术发展现状",
        "4:智能驾驶技术应用前景",
        "5:新能源汽车充电基础设施建设",
        "6:新能源汽车续航里程对比分析",
        "7:新能源汽车价格竞争力研究",
        "8:新能源汽车品牌营销策略分析",
        "9:新能源汽车用户购买决策因素",
        "10:新能源汽车技术创新方向",
        "11:新能源汽车产业链发展现状",
        "12:新能源汽车政策支持分析",
        "13:新能源汽车消费者行为研究",
        "14:新能源汽车品牌口碑对比",
        "15:新能源汽车市场细分分析",
        "16:新能源汽车国际化发展",
        "17:新能源汽车智能化水平评估",
        "18:新能源汽车安全性分析",
        "19:新能源汽车环保效益研究",
        "20:新能源汽车未来发展趋势",
        "21:新能源汽车产业链协同发展",
      ],
      firstMentionRate: [
        "1:用户首选新能源品牌推荐",
        "2:新能源汽车品牌知名度调查",
        "3:用户对新能源品牌的第一印象",
        "4:新能源汽车品牌影响力分析",
        "5:用户购买新能源汽车的考虑因素",
        "6:新能源汽车品牌认知度排名",
        "7:用户首次接触新能源汽车的渠道",
        "8:新能源汽车品牌记忆点分析",
        "9:用户对新能源汽车品牌的初始印象",
        "10:新能源汽车品牌差异化优势",
        "11:用户选择新能源汽车品牌的原因",
        "12:新能源汽车品牌传播效果评估",
        "13:用户对新能源汽车品牌的信任度",
        "14:新能源汽车品牌形象塑造",
        "15:用户对新能源汽车品牌的期待",
        "16:新能源汽车品牌竞争格局",
        "17:用户对新能源汽车品牌的忠诚度",
        "18:新能源汽车品牌价值主张",
        "19:用户对新能源汽车品牌的满意度",
        "20:新能源汽车品牌发展潜力",
        "21:新能源汽车品牌国际化战略",
      ],
      firstRecommendationRate: [
        "1:新能源汽车推荐购买指南",
        "2:用户最推荐的新能源汽车品牌",
        "3:新能源汽车购买建议",
        "4:新能源汽车品牌推荐度排名",
        "5:用户推荐新能源汽车的原因",
        "6:新能源汽车性价比推荐",
        "7:新能源汽车适合不同人群的推荐",
        "8:新能源汽车使用场景推荐",
        "9:新能源汽车技术特点推荐",
        "10:新能源汽车售后服务推荐",
        "11:新能源汽车充电便利性推荐",
        "12:新能源汽车保值率推荐",
        "13:新能源汽车安全性推荐",
        "14:新能源汽车智能化推荐",
        "15:新能源汽车环保性推荐",
        "16:新能源汽车品牌信誉推荐",
        "17:新能源汽车创新技术推荐",
        "18:新能源汽车用户体验推荐",
        "19:新能源汽车综合实力推荐",
        "20:新能源汽车未来价值推荐",
        "21:新能源汽车长期投资价值推荐",
      ],
      positiveMentionRate: [
        "1:用户对新能源汽车的正面评价",
        "2:新能源汽车的优势特点",
        "3:用户满意的新能源汽车功能",
        "4:新能源汽车的积极影响",
        "5:用户对新能源汽车的好评",
        "6:新能源汽车技术创新亮点",
        "7:新能源汽车用户体验优势",
        "8:新能源汽车环保贡献",
        "9:新能源汽车智能化优势",
        "10:新能源汽车经济性优势",
        "11:新能源汽车安全性优势",
        "12:新能源汽车便利性优势",
        "13:新能源汽车品牌形象优势",
        "14:新能源汽车服务优势",
        "15:新能源汽车性能优势",
        "16:新能源汽车设计优势",
        "17:新能源汽车质量优势",
        "18:新能源汽车可靠性优势",
        "19:新能源汽车创新优势",
        "20:新能源汽车发展前景优势",
        "21:新能源汽车社会价值优势",
      ],
      negativeMentionRate: [
        "1:用户对新能源汽车的负面反馈",
        "2:新能源汽车存在的问题",
        "3:用户不满意的新能源汽车方面",
        "4:新能源汽车的改进建议",
        "5:用户对新能源汽车的抱怨点",
        "6:新能源汽车技术缺陷分析",
        "7:新能源汽车用户体验问题",
        "8:新能源汽车充电问题",
        "9:新能源汽车续航焦虑",
        "10:新能源汽车价格问题",
        "11:新能源汽车维修成本",
        "12:新能源汽车保值率问题",
        "13:新能源汽车安全性担忧",
        "14:新能源汽车充电设施不足",
        "15:新能源汽车电池寿命问题",
        "16:新能源汽车售后服务问题",
        "17:新能源汽车智能化不足",
        "18:新能源汽车品牌信任度问题",
        "19:新能源汽车政策依赖问题",
        "20:新能源汽车市场接受度挑战",
        "21:新能源汽车技术标准不统一问题",
      ],
    };

    // 根据 metricType 选择问题模板
    const metricTypes = metricType.split(",").map((m) => m.trim());
    const allQuestions: QuestionItem[] = [];

    metricTypes.forEach((metric) => {
      const templates =
        questionTemplates[metric as keyof typeof questionTemplates] || [];
      templates.forEach((question, index) => {
        allQuestions.push({
          taskId: `task-${metric}-${index}-${Date.now()}`,
          qId: `q-${metric}-${index}-${Date.now()}`,
          question,
          modelId: params.modelId || "deepseek",
          modelName: "Deepseek",
          createTime: Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000, // 随机7天内的时间
        });
      });
    });

    // 分页处理
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const list = allQuestions.slice(startIndex, endIndex);

    return {
      list,
      total: allQuestions.length,
      page,
      pageSize,
    };
  },

  /**
   * 查询问题列表 Mock 数据
   */
  async getQueryQuestions(params: QueryQuestionsParams): Promise<{
    list: QueryQuestionItem[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    await randomDelay();

    const { page = 1, pageSize = 20, metricType = "mentionRate" } = params;

    // 根据 metricType 生成不同的问题
    const questionTemplates = {
      mentionRate: [
        "1:用户对主流新能源品牌的喜爱和抱怨点",
        "2:新能源汽车市场发展趋势分析",
        "3:电动汽车技术发展现状",
      ],
      firstMentionRate: [
        "1:用户首选新能源品牌推荐",
        "2:新能源汽车品牌知名度调查",
        "3:用户对新能源品牌的第一印象",
      ],
      firstRecommendationRate: [
        "1:新能源汽车推荐购买指南",
        "2:用户最推荐的新能源汽车品牌",
        "3:新能源汽车购买建议",
      ],
      positiveMentionRate: [
        "1:用户对新能源汽车的正面评价",
        "2:新能源汽车的优势特点",
        "3:用户满意的新能源汽车功能",
      ],
      negativeMentionRate: [
        "1:用户对新能源汽车的负面反馈",
        "2:新能源汽车存在的问题",
        "3:用户不满意的新能源汽车方面",
      ],
    };

    // 根据 metricType 选择问题模板
    const metricTypes = (metricType || "mentionRate")
      .split(",")
      .map((m) => m.trim());
    const allQuestions: QueryQuestionItem[] = [];

    metricTypes.forEach((metric) => {
      const templates =
        questionTemplates[metric as keyof typeof questionTemplates] || [];
      templates.forEach((question, index) => {
        allQuestions.push({
          taskId: `task-${metric}-${index}-${Date.now()}`,
          qId: `q-${metric}-${index}-${Date.now()}`,
          question,
          modelId: params.modelId || "deepseek",
          modelName: "Deepseek",
          createTime: Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
        });
      });
    });

    // 分页处理
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const list = allQuestions.slice(startIndex, endIndex);

    return {
      list,
      total: allQuestions.length,
      page,
      pageSize,
    };
  },

  /**
   * 媒体文章列表 Mock 数据
   */
  async getMediaArticles(params: any): Promise<any> {
    await randomDelay();

    const { page = 1, pageSize = 10 } = params;

    // 生成模拟文章数据
    const articles = Array.from({ length: pageSize }, (_, index) => {
      const globalIndex = (page - 1) * pageSize + index;
      return {
        title: `媒体引用文章标题 ${globalIndex + 1}`,
        url: `https://example.com/article/${globalIndex + 1}`,
        rate: Math.floor(Math.random() * 100),
      };
    });

    return {
      code: 200,
      message: "success",
      data: {
        list: articles,
        total: 100,
        page,
        pageSize,
      },
    };
  },

  /**
   * 意见反馈 Mock 数据
   */
  async submitFeedback(params: FeedbackRequest): Promise<FeedbackResponse> {
    await randomDelay(300, 800);

    console.log("=== 意见反馈Mock响应 ===");
    console.log("响应时间:", new Date().toLocaleString("zh-CN"));
    console.log("Mock参数:", JSON.stringify(params, null, 2));

    // 模拟成功响应 - 符合新接口规范
    const response: FeedbackResponse = {
      code: 200,
      message: "反馈成功",
      data: {},
    };

    console.log("Mock响应数据:", JSON.stringify(response, null, 2));
    return response;
  },
};

export default mockManager;
